<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { format } from 'date-fns';

import EnterpriseAccountAPI from 'dashboard/api/enterprise/account';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import PlanCard from './components/PlanCard.vue';
import PlanChangeDialog from './components/PlanChangeDialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  shouldUsePlanChange,
  inferChangeType,
} from './planChangeHelpers';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { currentAccount, accountId } = useAccount();

const interval = ref('monthly');
const isYearly = computed({
  get: () => interval.value === 'yearly',
  set: value => {
    interval.value = value ? 'yearly' : 'monthly';
  },
});

const selectingPlan = ref(null);
const plans = ref([]);
const isLoadingPlans = ref(true);
const plansLoadFailed = ref(false);
const planChangeDialogRef = ref(null);
const planChangePreview = ref(null);
const isLoadingPreview = ref(false);
const isConfirmingChange = ref(false);
const selectedPlan = ref(null);

const breadcrumbItems = computed(() => [
  {
    label: t('BILLING_SETTINGS.TITLE'),
    routeName: 'billing_settings_index',
  },
  { label: t('BILLING_SETTINGS.PLANS.TITLE') },
]);

const handleBreadcrumbClick = item => {
  if (item.routeName) {
    router.push({ name: item.routeName });
  }
};

const billing = computed(() => currentAccount.value?.billing || {});
const currentPlanName = computed(() => billing.value.plan_name || '');

const currentBillingInterval = computed(
  () => billing.value.interval || 'monthly'
);

const pendingPlan = computed(() => billing.value.pending_plan || null);

const pendingPlanEffectiveDate = computed(() => {
  if (!pendingPlan.value?.effective_at) return '';

  return format(new Date(pendingPlan.value.effective_at), 'dd MMM yyyy');
});

const isPendingPlan = planName =>
  pendingPlan.value?.name === planName &&
  !!pendingPlan.value?.effective_at &&
  (pendingPlan.value.interval || 'monthly') === interval.value;

const normalizePlan = plan => ({
  name: plan.name,
  includedTickets: plan.included_tickets,
  overageRate: plan.overage_rate,
  basePriceMonthly: plan.base_price_monthly,
  basePriceYearly: plan.base_price_yearly,
  seatCap: plan.seat_cap,
  monthlyOnly: plan.monthly_only,
  contactSales: plan.contact_sales,
});

const fetchPlans = async () => {
  isLoadingPlans.value = true;
  plansLoadFailed.value = false;
  try {
    const { data } = await EnterpriseAccountAPI.getPlans(accountId.value);
    plans.value = (data.plans || []).map(normalizePlan);
  } catch (error) {
    plansLoadFailed.value = true;
    useAlert(error?.message || t('BILLING_SETTINGS.PLANS.LOAD_FAILED'));
  } finally {
    isLoadingPlans.value = false;
  }
};

const loadAccount = async () => {
  await store.dispatch('accounts/get', { silent: true });
  await store.dispatch('accounts/limits');
};

onMounted(async () => {
  if (route.query.checkout === 'success' || route.query.upgrade === 'success') {
    await store.dispatch('accounts/syncCheckout');
  }

  await Promise.all([fetchPlans(), loadAccount()]);

  if (billing.value.interval) {
    interval.value = billing.value.interval;
  }

  if (route.query.upgrade === 'success') {
    useAlert(t('BILLING_SETTINGS.PLAN_CHANGE.UPGRADE_SUCCESS'));
    router.replace({ name: 'billing_settings_plans', query: {} });
  } else if (route.query.checkout === 'success') {
    router.replace({ name: 'billing_settings_plans', query: {} });
  }
});

const startCheckout = async plan => {
  selectingPlan.value = plan.name;
  try {
    await store.dispatch('accounts/planCheckout', {
      plan: plan.name,
      interval: interval.value,
    });
  } catch (error) {
    useAlert(error?.message || t('BILLING_SETTINGS.PLAN_CHANGE.CHECKOUT_FAILED'));
  } finally {
    selectingPlan.value = null;
  }
};

const openPlanChange = async plan => {
  selectedPlan.value = plan;
  planChangePreview.value = null;
  isLoadingPreview.value = true;

  try {
    planChangePreview.value = await store.dispatch('accounts/planChangePreview', {
      plan: plan.name,
      interval: interval.value,
    });
    planChangeDialogRef.value?.open();
  } catch (error) {
    selectedPlan.value = null;
    useAlert(error?.message || t('BILLING_SETTINGS.PLAN_CHANGE.PREVIEW_FAILED'));
  } finally {
    isLoadingPreview.value = false;
  }
};

const onPlanChangeDialogClose = () => {
  selectedPlan.value = null;
  planChangePreview.value = null;
};

const confirmPlanChange = async () => {
  if (!selectedPlan.value) return;

  const planName = selectedPlan.value.name;
  const changeType =
    planChangePreview.value?.change_type ||
    planChangePreview.value?.changeType ||
    inferChangeType(currentPlanName.value, planName, {
      currentInterval: currentBillingInterval.value,
      targetInterval: interval.value,
    });
  const isUpgrade = changeType === 'upgrade';

  isConfirmingChange.value = true;
  try {
    if (isUpgrade) {
      await store.dispatch('accounts/planUpgradeCheckout', {
        plan: planName,
        interval: interval.value,
      });
      return;
    }

    await store.dispatch('accounts/planChange', {
      plan: planName,
      interval: interval.value,
    });

    planChangeDialogRef.value?.close();
    useAlert(t('BILLING_SETTINGS.PLAN_CHANGE.DOWNGRADE_SUCCESS'));
    await loadAccount();
    selectedPlan.value = null;
    planChangePreview.value = null;
  } catch (error) {
    useAlert(error?.message || t('BILLING_SETTINGS.PLAN_CHANGE.CHECKOUT_FAILED'));
    await loadAccount();
  } finally {
    isConfirmingChange.value = false;
  }
};

const onSelectPlan = async plan => {
  if (plan.contactSales) {
    window.location.href = 'mailto:sales@custio.io?subject=Enterprise%20plan';
    return;
  }

  if (
    interval.value === 'yearly' &&
    (plan.monthlyOnly || !plan.basePriceYearly)
  ) {
    return;
  }

  if (
    shouldUsePlanChange({
      account: currentAccount.value,
      billing: billing.value,
      currentPlanName: currentPlanName.value,
      targetPlanName: plan.name,
      currentInterval: currentBillingInterval.value,
      targetInterval: interval.value,
    })
  ) {
    await openPlanChange(plan);
    return;
  }

  await startCheckout(plan);
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoadingPlans"
    :no-records-found="!isLoadingPlans && !plansLoadFailed && plans.length === 0"
    :no-records-message="$t('BILLING_SETTINGS.PLANS.EMPTY')"
  >
    <template #header>
      <div class="flex flex-col gap-4 w-full">
        <Breadcrumb
          :items="breadcrumbItems"
          @click="handleBreadcrumbClick"
        />
        <BaseSettingsHeader
          :title="$t('BILLING_SETTINGS.PLANS.TITLE')"
          :description="$t('BILLING_SETTINGS.PLANS.DESCRIPTION')"
        />
      </div>
    </template>

    <template #body>
      <div class="flex items-center justify-center gap-3 mb-8">
        <span
          class="text-sm font-medium"
          :class="
            interval === 'monthly' ? 'text-n-slate-12' : 'text-n-slate-11'
          "
        >
          {{ $t('BILLING_SETTINGS.PLANS.MONTHLY') }}
        </span>

        <Switch v-model="isYearly" />

        <span
          class="text-sm font-medium"
          :class="
            interval === 'yearly' ? 'text-n-slate-12' : 'text-n-slate-11'
          "
        >
          {{ $t('BILLING_SETTINGS.PLANS.ANNUAL') }}
        </span>
      </div>

      <div
        class="grid gap-4 xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2 grid-cols-1"
      >
        <PlanCard
          v-for="plan in plans"
          :key="plan.name"
          :plan="plan"
          :interval="interval"
          :current-billing-interval="currentBillingInterval"
          :is-pending="isPendingPlan(plan.name)"
          :pending-effective-date="pendingPlanEffectiveDate"
          :current-plan-name="currentPlanName || ''"
          :is-loading="selectingPlan === plan.name || (isLoadingPreview && selectedPlan?.name === plan.name)"
          @select="onSelectPlan"
        />
      </div>
    </template>
  </SettingsLayout>

  <PlanChangeDialog
    ref="planChangeDialogRef"
    :preview="planChangePreview"
    :target-plan-name="selectedPlan?.name || ''"
    :current-plan-name="currentPlanName || ''"
    :current-interval="currentBillingInterval"
    :target-interval="interval"
    :is-loading="isLoadingPreview"
    :is-confirming="isConfirmingChange"
    @confirm="confirmPlanChange"
    @close="onPlanChangeDialogClose"
  />
</template>
